import React, { useState } from 'react';
import { Typography, Box, Paper, Button, CircularProgress, Alert, 
  List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

const OutstandingUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    
    if (!selectedFile) {
      setFile(null);
      return;
    }
    
    // Check file type
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError('Only Excel files (.xlsx, .xls) are allowed');
      setFile(null);
      return;
    }
    
    // Clear error if valid file
    setError(null);
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/api/uploads/outstanding', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadResult(response.data);
      toast.success('File uploaded and processed successfully');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Extract specific error message if available
      let errorMessage = 'Failed to upload file. Please try again.';
      
      if (error.response?.data?.error) {
        // If the server sent a specific error message, use it
        errorMessage = error.response.data.error;
        
        // Special handling for missing columns error
        if (errorMessage.includes('Missing required columns')) {
          errorMessage = `${errorMessage}. Please download the template and follow the format exactly.`;
        }
      }
      
      setError(errorMessage);
      toast.error('Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/uploads/history');
  };

  const handleViewHistory = () => {
    navigate('/uploads/history');
  };
  
  const handleDownloadTemplate = () => {
    // Create a direct link to the backend endpoint
    const templateUrl = `${api.defaults.baseURL}/api/uploads/outstanding/template`;
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = templateUrl;
    link.setAttribute('download', 'outstanding_template.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.info('Downloading template...');
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={4}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">Upload Outstanding Data</Typography>
      </Box>
      
      {/* Instructions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6">
            Instructions
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
          >
            Download Template
          </Button>
        </Box>
        <Typography variant="body1" paragraph>
          Upload an Excel file (.xlsx or .xls) containing your outstanding data.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>The Excel file should contain the following columns:</strong>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Customer Name (required)" 
                secondary="Customer's business name"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="GSTIN (required)" 
                secondary="Customer's GST Identification Number"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Outstanding Amount (required)" 
                secondary="Amount in INR"
              />
            </ListItem>
          </List>
        </Typography>
      </Paper>
      
      {/* Upload form */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box 
          border={2} 
          borderColor={error ? 'error.main' : 'divider'} 
          borderRadius={1}
          borderStyle="dashed"
          p={5}
          mb={3}
          textAlign="center"
          bgcolor="background.default"
        >
          <input
            accept=".xlsx,.xls"
            id="excel-upload"
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <label htmlFor="excel-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={loading}
            >
              Select Excel File
            </Button>
          </label>
          
          {file && (
            <Typography variant="body1" mt={2}>
              Selected file: {file.name}
            </Typography>
          )}
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!file || loading}
            sx={{ minWidth: 200 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Upload & Process'}
          </Button>
        </Box>
      </Paper>
      
      {/* Upload results */}
      {uploadResult && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            <Typography variant="h6">
              File processed successfully
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Typography variant="body1">
            <strong>Total records:</strong> {uploadResult.stats.total}
          </Typography>
          <Typography variant="body1">
            <strong>Successfully processed:</strong> {uploadResult.stats.success}
          </Typography>
          <Typography variant="body1">
            <strong>Failed records:</strong> {uploadResult.stats.failed}
          </Typography>
          
          <Box mt={3}>
            <Button 
              variant="outlined" 
              onClick={handleViewHistory}
            >
              View Upload History
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default OutstandingUpload;